import ReactDOM from "react-dom/client";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/FullScreenLoader";
import NotificationEventTypes from "../../constants/notificationEventsEnum";
import { useWorkflow } from "../../context/WorkflowContext";

function ReviewDetailsModal({
  selectedRowData,
  onClose,
  firstRegistrationId,
  refreshOnSubmitSuccess,
  mode = "acceptance",
}) {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");
  const organizationId = localStorage.getItem("organizationId");

  const { workflowParams } = useWorkflow();

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm();

  const [feePlans, setFeePlans] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentData, setAssessmentData] = useState([]);
  const [existingFeePlan, setExistingFeePlan] = useState(null);

  const selectedFeePlan = feePlans.find(
    (plan) => plan.id === selectedRowData.centerFeePlanId
  );
  const selectedConcession = concessions.find(
    (concession) => concession.id === selectedRowData.centerConcessionId
  );
  const selectedFeePlanName = selectedFeePlan
    ? `${selectedFeePlan.name} (₹${selectedFeePlan.totalAmount})`
    : "No fee plan assigned";
  const selectedConcessionName = selectedConcession
    ? `${selectedConcession.name} (${selectedConcession.concessionPercentage}%)`
    : "No concession assigned";

  useEffect(() => {
    if (selectedRowData) {
      const hasFeePlan =
        selectedRowData.centerFeePlanId &&
        selectedRowData.centerFeePlanId !== 0;

      const initialValues = {
        isInterviewTaken: selectedRowData.isInterviewTaken ? "Yes" : "No",
        interviewDate: selectedRowData.interviewDate
          ? selectedRowData.interviewDate.split("T")[0]
          : "",
        interviewRemarks: selectedRowData.interviewRemarks || "",
        admissionStatus: selectedRowData.isAccepted
          ? "isAccepted"
          : selectedRowData.hasObservation
          ? "hasObservation"
          : selectedRowData.shouldRetakeExam
          ? "shouldRetakeExam"
          : "",
        remarks:
          selectedRowData.acceptanceRemarks ||
          selectedRowData.observationRemarks ||
          selectedRowData.retakeExamRemarks ||
          "",
        feePlanId: hasFeePlan ? selectedRowData.centerFeePlanId : "",
        concessionId: hasFeePlan ? selectedRowData.centerConcessionId : "",
        assessmentDate: "",
        communicationRemarks: "",
        approvalStatus: "",
        approvalRemarks: "",
      };

      // Set approval fields based on mode
      if (mode === "ceoApproval") {
        initialValues.approvalStatus = selectedRowData.isCEOApproved
          ? "approved"
          : "rejected";
        initialValues.approvalRemarks =
          selectedRowData.ceoApprovalRemarks || "";
      } else if (mode === "accountsApproval") {
        initialValues.approvalStatus = selectedRowData.isAccountApproved
          ? "approved"
          : "rejected";
        initialValues.approvalRemarks =
          selectedRowData.accountApprovalRemarks || "";
      }

      reset(initialValues);

      if (hasFeePlan) {
        setExistingFeePlan({
          feePlanId: selectedRowData.centerFeePlanId,
          concessionId: selectedRowData.centerConcessionId,
          feePlanSubmissionDate: selectedRowData.feePlanSubmissionDate,
          feePlanUpdateDate: new Date().toISOString(),
        });
      }
    }
  }, [selectedRowData, mode, reset]);

  useEffect(() => {
    const fetchFeePlansAndConcessions = async () => {
      if (!selectedRowData || !centerId || !selectedRowData.centerClassId) {
        toast.error(
          "Missing required data for fetching fee plans or concessions."
        );
        return;
      }

      setIsLoading(true);
      try {
        const feePlanResponse = await get(
          `FeePlan/GetAllFeePlanByCenterAndClassIdAsync?centerId=${centerId}&centerClassId=${selectedRowData.centerClassId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (Array.isArray(feePlanResponse)) {
          setFeePlans(feePlanResponse);
        } else {
          toast.error("Invalid fee plan data received.");
        }
      } catch (error) {
        console.error("Error fetching fee plans:", error);
        toast.error("Failed to fetch fee plans.");
      }

      try {
        const concessionResponse = await get(
          `ConcessionType/GetAllConcessionByCenterClassId?centerId=${centerId}&centerClassId=${selectedRowData.centerClassId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (
          concessionResponse.isSuccess &&
          Array.isArray(concessionResponse.concessionByCenterClass)
        ) {
          setConcessions(concessionResponse.concessionByCenterClass);
        } else {
          toast.error("Invalid concession data received.");
        }
      } catch (error) {
        console.error("Error fetching concessions:", error);
        toast.error("Failed to fetch concessions.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeePlansAndConcessions();
  }, [selectedRowData, centerId, token]);

  useEffect(() => {
    const fetchAssessmentDetails = async () => {
      try {
        const response = await get(
          `AssessmentType/GetStudentAssessmentByRegId?studentFirstRegistrationId=${firstRegistrationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.studentAssessmentAndInterviewDetails) {
          setAssessmentData(response.studentAssessmentAndInterviewDetails);
        } else {
          toast.error("Invalid assessment data received.");
        }
      } catch (error) {
        console.error("Error fetching assessment details:", error);
        toast.error("Failed to fetch assessment details.");
      }
    };

    fetchAssessmentDetails();
  }, [firstRegistrationId, token]);

  const saveAdmissionStatus = async (data) => {
    const currentDate = new Date().toISOString();
    const userId = localStorage.getItem("userId");

    if (mode === "accountsApproval") {
      const formData = {
        id: firstRegistrationId,
        isApproved: data.approvalStatus === "approved",
        approvalDate: currentDate,
        approvalStatus: data.approvalStatus,
        approvalRemarks: data.approvalRemarks,
        department: "accounts",
        // NotificationEventTypeIds:
        //   data.approvalStatus === "approved"
        //     ? NotificationEventTypes.APPROVE.toString()
        //     : data.approvalStatus === "rejected"
        //     ? NotificationEventTypes.REJECT.toString()
        //     : "",
        // FormId: 22,
        Workflow: {
          ...workflowParams.Workflow,
          WorkflowStepId: workflowParams.Workflow.WorkflowStepId,
          CreatedBy: Number(localStorage.getItem("userId")),
        },
      };

      // console.log(formData);
      // return;

      try {
        const response = await post(
          "Student/UpdateStudentAccountDepartmentStatuswiseById",
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.isSuccess) {
          toast.success(
            response?.message || "Approval status updated successfully!"
          );
          onClose();
          refreshOnSubmitSuccess();
        } else {
          toast.error(response?.message || "Failed to update approval status.");
        }
      } catch (error) {
        console.error("Error updating approval status:", error);
        toast.error("An error occurred while updating approval status.");
      }
    } else if (mode === "ceoApproval") {
      const formData = {
        id: firstRegistrationId,
        isApproved: data.approvalStatus === "approved",
        approvalDate: currentDate,
        approvalStatus: data.approvalStatus,
        approvalRemarks: data.approvalRemarks,
        department: "CEO",
        NotificationEventTypeIds:
          data.approvalStatus === "approved"
            ? NotificationEventTypes.APPROVE.toString()
            : data.approvalStatus === "rejected"
            ? NotificationEventTypes.REJECT.toString()
            : "",
        FormId: 22,
        Workflow: {
          ...workflowParams.Workflow,
          WorkflowStepId: workflowParams.Workflow.WorkflowStepId,
          CreatedBy: Number(localStorage.getItem("userId")),
        },
      };

      // console.log(formData);
      // return;

      try {
        const response = await post(
          "Student/UpdateStudentAccountDepartmentStatuswiseById",
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.isSuccess) {
          toast.success(
            response?.message || "CEO approval updated successfully!"
          );
          onClose();
          refreshOnSubmitSuccess();
        } else {
          toast.error(response?.message || "Failed to update CEO approval.");
        }
      } catch (error) {
        console.error("Error updating CEO approval:", error);
        toast.error("An error occurred while updating CEO approval.");
      }
    } else {
      let formData = {
        id: firstRegistrationId,
        FormId: 79,
        NotificationEventTypeIds: NotificationEventTypes.ADD.toString(),
        updatedOn: currentDate,
        updatedBy: userId,
        Workflow: {
          ...workflowParams.Workflow,
          WorkflowStepId: workflowParams.Workflow.WorkflowStepId,
          CreatedBy: Number(localStorage.getItem("userId")),
        },
      };

      if (mode === "acceptance") {
        const isAccepted = data.admissionStatus === "isAccepted";
        formData = {
          ...formData,
          isAccepted: isAccepted,
          acceptanceRemarks: isAccepted ? data.remarks : "",
          hasObservation: data.admissionStatus === "hasObservation",
          observationRemarks:
            data.admissionStatus === "hasObservation" ? data.remarks : "",
          shouldRetakeExam: data.admissionStatus === "shouldRetakeExam",
          retakeExamRemarks:
            data.admissionStatus === "shouldRetakeExam" ? data.remarks : "",
          centerFeePlanId: isAccepted ? data.feePlanId : 0,
          centerConcessionId:
            isAccepted && data.concessionId !== "" ? data.concessionId : 0,
          feePlanSubmissionDate: isAccepted
            ? existingFeePlan?.feePlanSubmissionDate || currentDate
            : null,
          feePlanSubmittedBy: isAccepted
            ? existingFeePlan?.feePlanSubmittedBy || userId
            : "",
          feePlanUpdateDate: isAccepted ? currentDate : null,
          feePlanUpdatedBy: isAccepted ? userId : "",
        };

        if (data.admissionStatus === "shouldRetakeExam") {
          const assessmentFormData = {
            id: firstRegistrationId,
            assessmentDate: data.assessmentDate,
            communicationRemarks: data.communicationRemarks,
            isCommunicatedToGuardian: true,
            centerClassId: selectedRowData?.centerClassId,
            updatedBy: userId,
          };
          try {
            const assessmentResponse = await post(
              "Student/UpdateFirstRegistrationAssessmentDate",
              assessmentFormData,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (assessmentResponse?.isSuccess) {
              toast.success(
                assessmentResponse?.message ||
                  "Assessment details updated successfully!"
              );
            } else {
              toast.error(
                assessmentResponse?.message ||
                  "Failed to update assessment details."
              );
            }
          } catch (error) {
            console.error("Error updating assessment details:", error);
            toast.error("An error occurred while updating assessment details.");
          }
        }
      }

      // console.log(formData);
      // return;

      try {
        const response = await post(
          "Student/UpdateStudentAdmissionStatus",
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.isSuccess) {
          toast.success(
            response?.message || "Admission status updated successfully!"
          );
          onClose();
          refreshOnSubmitSuccess();
        } else {
          toast.error(response?.message || "Failed to update status.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("An error occurred while updating status.");
      }
    }
  };

  return (
    <>
      {isLoading && <FullScreenLoader />}
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">
                {mode === "acceptance"
                  ? "Acceptance Status"
                  : mode === "accountsApproval"
                  ? "Accounts Approval"
                  : "CEO Approval"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveAdmissionStatus)}
              >
                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <h6 className="mb-2">Assessment Details</h6>
                      <table className="table table-bordered custom-table m-0">
                        <thead>
                          <tr>
                            <th>Assessment Type</th>
                            <th>Obtained Score</th>
                            <th>Passing Score (%)</th>
                            <th>Total Score</th>
                            <th>Result Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!assessmentData.length ? (
                            <tr className="text-center w-100">
                              <td colSpan="5">
                                There are no records to display
                              </td>
                            </tr>
                          ) : (
                            assessmentData.map((item, index) => (
                              <tr key={index}>
                                <td>{item.assessmentTypeName}</td>
                                <td>{item.obtainedScore}</td>
                                <td>{item.passingScorePercentage}</td>
                                <td>{item.totalScore}</td>
                                <td>{item.resultStatus}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="col-12">
                      <h6 className="mb-2">Interview Details</h6>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label form-label-new">
                            Interview Taken
                          </label>
                          <select
                            disabled
                            className="form-select"
                            {...register(`isInterviewTaken`, {
                              required: "This field is required.",
                            })}
                          >
                            <option value="">Select</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                          {errors.isInterviewTaken && (
                            <small className="text-danger">
                              {errors.isInterviewTaken.message}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label form-label-new">
                            Interview Date
                          </label>
                          <input
                            disabled
                            type="date"
                            {...register(`interviewDate`, {
                              required: "This field is required.",
                            })}
                            className="form-control"
                          />
                          {errors.interviewDate && (
                            <small className="text-danger">
                              {errors.interviewDate.message}
                            </small>
                          )}
                        </div>
                        <div className="col-md-12">
                          <label className="form-label form-label-new">
                            Interview Remarks
                          </label>
                          <textarea
                            disabled
                            {...register(`interviewRemarks`, {
                              required: "This field is required.",
                            })}
                            className="form-control"
                          ></textarea>
                          {errors.interviewRemarks && (
                            <small className="text-danger">
                              {errors.interviewRemarks.message}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <h6 className="mb-2">
                    {mode === "acceptance"
                      ? "Admission Status"
                      : "Fee Plan Approval"}
                  </h6>
                  <div className="row g-3">
                    {mode === "acceptance" ? (
                      <>
                        <div className="col-md-4">
                          <label className="form-label form-label-new">
                            Admission Status{" "}
                            <span className="text-danger fs-9">*</span>
                          </label>
                          <select
                            className="form-select"
                            {...register(`admissionStatus`, {
                              required: "This field is required.",
                            })}
                          >
                            <option value="">Select</option>
                            <option value="isAccepted">Accept</option>
                            <option value="hasObservation">Observe</option>
                            <option value="shouldRetakeExam">
                              Retake Assessment
                            </option>
                          </select>
                          {errors.admissionStatus && (
                            <small className="text-danger">
                              {errors.admissionStatus.message}
                            </small>
                          )}
                        </div>
                        {(watch("admissionStatus") === "hasObservation" ||
                          watch("admissionStatus") === "shouldRetakeExam") && (
                          <div className="col-md-8">
                            <label className="form-label form-label-new">
                              Remarks{" "}
                              <span className="text-danger fs-9">*</span>
                            </label>
                            <textarea
                              {...register(`remarks`, {
                                required:
                                  watch("admissionStatus") ===
                                    "hasObservation" ||
                                  watch("admissionStatus") ===
                                    "shouldRetakeExam"
                                    ? "This field is required."
                                    : false,
                              })}
                              className="form-control"
                            ></textarea>
                            {errors.remarks && (
                              <small className="text-danger">
                                {errors.remarks.message}
                              </small>
                            )}
                          </div>
                        )}
                        {/* <div className="col-md-8">
                          <label className="form-label form-label-new">
                            Remarks <span className="text-danger fs-9">*</span>
                          </label>
                          <textarea
                            {...register(`remarks`, {
                              required: "This field is required.",
                            })}
                            className="form-control"
                          ></textarea>
                          {errors.remarks && (
                            <small className="text-danger">
                              {errors.remarks.message}
                            </small>
                          )}
                        </div> */}
                      </>
                    ) : (
                      <div className="col-12">
                        <h6 className="mb-2 fw-normal">
                          Admission Status: Accepted
                        </h6>
                        <h6 className="mb-2 fw-normal">
                          Fee Plan: {selectedFeePlanName}
                        </h6>
                        <h6 className="mb-2 fw-normal">
                          Concession: {selectedConcessionName}
                        </h6>
                        {mode === "ceoApproval" && selectedRowData && (
                          <div className="mt-3">
                            <h6 className="mb-2">
                              Accounts Department Approval
                            </h6>
                            <h6 className="mb-2 fw-normal">
                              Status:{" "}
                              {selectedRowData.isAccountApproved
                                ? "Approved"
                                : "Not Approved"}
                            </h6>
                            <h6 className="mb-2 fw-normal">
                              Remarks:{" "}
                              {selectedRowData.accountApprovalRemarks ||
                                "No remarks provided"}
                            </h6>
                            <h6 className="mb-2 fw-normal">
                              Date:{" "}
                              {selectedRowData.accountApprovalDate
                                ? new Date(
                                    selectedRowData.accountApprovalDate
                                  ).toLocaleDateString()
                                : "Not set"}
                            </h6>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {mode === "acceptance" &&
                    watch("admissionStatus") === "isAccepted" && (
                      <div className="row g-3 mt-2">
                        <div className="col-md-6">
                          <label className="form-label form-label-new">
                            Fee Plan <span className="text-danger fs-9">*</span>
                          </label>
                          <select
                            className="form-select"
                            {...register(`feePlanId`, {
                              required:
                                watch("admissionStatus") === "isAccepted"
                                  ? "This field is required."
                                  : false,
                            })}
                          >
                            <option value="">Select</option>
                            {feePlans.length ? (
                              feePlans.map((plan) => (
                                <option key={plan.id} value={plan.id}>
                                  {plan.name} (₹{plan.totalAmount})
                                  {existingFeePlan?.feePlanId === plan.id &&
                                    " (Current)"}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                No fee plans available
                              </option>
                            )}
                          </select>
                          {errors.feePlanId && (
                            <small className="text-danger">
                              {errors.feePlanId.message}
                              {existingFeePlan && " - Update existing plan"}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label form-label-new">
                            Concession{" "}
                            {/* <span className="text-danger fs-9">*</span> */}
                          </label>
                          <select
                            className="form-select"
                            {...register(`concessionId`, {
                              // required:
                              //   watch("admissionStatus") === "isAccepted"
                              //     ? "This field is required."
                              //     : false,
                            })}
                          >
                            <option value="">Select</option>
                            {concessions.length ? (
                              concessions.map((concession) => (
                                <option
                                  key={concession.id}
                                  value={concession.id}
                                >
                                  {concession.name} (
                                  {concession.concessionPercentage}%)
                                  {existingFeePlan?.concessionId ===
                                    concession.id && " (Current)"}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                No concessions available
                              </option>
                            )}
                          </select>
                          {errors.concessionId && (
                            <small className="text-danger">
                              {errors.concessionId.message}
                              {existingFeePlan &&
                                " - Update existing concession"}
                            </small>
                          )}
                        </div>
                        {existingFeePlan && (
                          <div className="col-12">
                            <div className="alert alert-info py-2 mb-0">
                              <i className="bi bi-info-circle me-2"></i>
                              This student already has a fee plan applied.
                              Selecting a new plan will update the existing one.
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  {(mode === "accountsApproval" || mode === "ceoApproval") && (
                    <div className="row g-3 mt-2">
                      <div className="col-6">
                        <label className="form-label form-label-new">
                          Approval Status{" "}
                          <span className="text-danger fs-8">*</span>
                        </label>
                        <select
                          className="form-select"
                          {...register(`approvalStatus`, {
                            required: "This field is required.",
                          })}
                        >
                          <option value="">Select</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Not Approved</option>
                        </select>
                        {errors.approvalStatus && (
                          <small className="text-danger">
                            {errors.approvalStatus.message}
                          </small>
                        )}
                      </div>
                      <div className="col-6">
                        <label className="form-label form-label-new">
                          Comments <span className="text-danger fs-8">*</span>
                        </label>
                        <textarea
                          {...register("approvalRemarks", {
                            required: "This field is required.",
                          })}
                          className="form-control"
                        ></textarea>
                        {errors.approvalRemarks && (
                          <small className="text-danger">
                            {errors.approvalRemarks.message}
                          </small>
                        )}
                      </div>
                    </div>
                  )}
                  {mode === "acceptance" &&
                    watch("admissionStatus") === "shouldRetakeExam" && (
                      <div className="row g-3 mt-2">
                        <div className="col-md-4">
                          <label className="form-label form-label-new">
                            Assessment Date{" "}
                            <span className="text-danger fs-8">*</span>
                          </label>
                          <input
                            type="date"
                            {...register("assessmentDate", {
                              required: "This field is required.",
                            })}
                            className="form-control"
                          />
                          {errors.assessmentDate && (
                            <small className="text-danger">
                              {errors.assessmentDate.message}
                            </small>
                          )}
                        </div>
                        <div className="col-md-8">
                          <label className="form-label form-label-new">
                            Retake Assessment Comments{" "}
                            <span className="text-danger fs-8">*</span>
                          </label>
                          <textarea
                            {...register("communicationRemarks", {
                              required: "This field is required.",
                            })}
                            className="form-control"
                          ></textarea>
                          {errors.communicationRemarks && (
                            <small className="text-danger">
                              {errors.communicationRemarks.message}
                            </small>
                          )}
                        </div>
                      </div>
                    )}
                </div>
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span
                        className="spinner-border spinner-custom-size me-1"
                        aria-hidden="true"
                      ></span>
                    ) : null}
                    <span role="status">Save</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default ReviewDetailsModal;
